import React, { FC, useDeferredValue, useEffect, useState } from 'react';
import {
  SideSheetBody,
  SideSheet,
  SideSheetHeader,
  SideSheetClose,
  SideSheetFooter,
  Box,
  SideSheetContentCol,
  Text,
  ButtonAppearance,
  theme,
  TextFieldSize,
  TextFieldAppearance,
  Select,
  FormControl,
  IconSize,
  Spinner,
  SpinnerSize,
  Checkbox,
  SideSheetSize,
  Button,
  ButtonSize,
  Icon,
} from '@bechtel/gds';
import { DrawerFooterButtonStyled, SearchField } from '@bechtel/client-common';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { AddCostCodeProps } from './types';
import { useLazyQuery } from '@apollo/client';
import { CostCode, CostCodeList, PagingInput, SearchCostCodeInput } from '@bechtel/types';
import { GET_COST_CODES } from '@bechtel/client-gql';
import { filter, findIndex, get, map, some } from 'lodash';
import { ReactComponent as PackageIcon } from '@bechtel/client-assets/dist/images/work-package.svg';
import { CostCodeBoxStyled, LabelStyled } from './styled';

const Item = ({ item }: any) => {
  const { i18n } = useLingui();
  return (
    <Text size="400" color="grey.1000" family="graphikCompact" style={{ whiteSpace: 'nowrap' }}>
      {i18n._(item?.name ? item.name : '')}
    </Text>
  );
};

const SelectedItem = ({ item }: any) => {
  const { i18n } = useLingui();
  return (
    <Text size="400" color="grey.1000" family="graphik" style={{ whiteSpace: 'nowrap' }}>
      {i18n._(item?.name ? item.name : '')}
    </Text>
  );
};

export const AddCostCode: FC<AddCostCodeProps> = ({ closeSidesheet, crewId, addCostCode, foreman, tsCostCodes }) => {
  const [costCodeSearchText, setCostCodeSearchText] = useState('');
  const [wpSearchText, setWPSearchText] = useState('');
  const [commoditySearchText, setCommoditySearchText] = useState('');
  const [crewMemberId, setCrewMemberId] = useState('');
  const deferredWPSearchValue = useDeferredValue(wpSearchText);
  const deferredCommoditySearchValue = useDeferredValue(commoditySearchText);
  const deferredCostCodeSearchValue = useDeferredValue(costCodeSearchText);
  const [selectedCostCodes, setSelectedCostCodes] = useState<CostCode[]>([]);
  const pageLimit = 20;

  const costCodeFilterScope: { name: string; value: string }[] = [
    {
      name: t`All Cost Codes`,
      value: '',
    },
    {
      name: t`Assigned Scope`,
      value: foreman?.id,
    },
  ];

  const [getCostCodes, { data, error, loading, refetch, fetchMore }] = useLazyQuery<
    { costCodes: CostCodeList },
    { input: SearchCostCodeInput; paging: PagingInput }
  >(GET_COST_CODES, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  useEffect(() => {
    getCostCodes({
      variables: {
        input: {
          crewId,
          crewMemberId,
          workPackageNumber: wpSearchText,
          costCode: costCodeSearchText,
          commodityTag: commoditySearchText,
        },
        paging: {
          cursor: '1',
          limit: pageLimit,
        },
      },
    });
  }, [deferredWPSearchValue, deferredCommoditySearchValue, deferredCostCodeSearchValue, crewMemberId, crewId]);

  const costCodes: CostCode[] = get(data, 'costCodes.items', []);

  const totalPages = Math.ceil(get(data, 'costCodes.total', 1) / pageLimit);
  const cursor: number = get(data, 'costCodes.cursor', 1);

  const handleScroll = (e) => {
    if (e.target.scrollTop + e.target.clientHeight + 200 >= e.target.scrollHeight && cursor <= totalPages && !loading) {
      fetchMore({
        variables: {
          paging: { cursor: cursor.toString(), limit: pageLimit },
        },
      });
    }
  };

  const addSelectedCostCode = () => {
    addCostCode(selectedCostCodes);
    closeSidesheet();
  };

  const checkSelectedCostCode = (costCode: CostCode) => {
    return findIndex(tsCostCodes, (item: CostCode) => item.id === costCode.id) === -1 ? false : true;
  };

  return (
    <SideSheet onDismiss={closeSidesheet} size={SideSheetSize.MEDIUM} visible>
      <SideSheetHeader title={t`Add New Cost Code`} data-testid="add-cost-code-header">
        <SideSheetClose onClick={closeSidesheet}></SideSheetClose>
      </SideSheetHeader>
      <SideSheetBody>
        <SideSheetContentCol style={{ flexGrow: 1, borderRight: `1px solid ${theme.colors.grey[300]}` }}>
          <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
            <Box mb={{ sm: '600' }} flexGrow={{ sm: 1 }}>
              <FormControl style={{ flexGrow: 1 }} label={t`Cost Code Filter`}>
                <Select
                  size={TextFieldSize.MEDIUM}
                  appearance={TextFieldAppearance.NORMAL}
                  items={costCodeFilterScope}
                  itemComponent={Item}
                  selectedComponent={SelectedItem}
                  initialSelectedItem={costCodeFilterScope[0]}
                  onSelectedItemChange={(changes: any) => {
                    setCrewMemberId(changes.selectedItem.value);
                  }}
                />
              </FormControl>
            </Box>
            <Box mb={{ sm: '600' }} flexGrow={{ sm: 1 }}>
              <FormControl style={{ flexGrow: 1 }} label={t`Search by Work Package`}>
                <SearchField
                  size={TextFieldSize.MEDIUM}
                  iconSize={IconSize.MEDIUM}
                  type="search"
                  onChange={(e) => {
                    setWPSearchText(e.target.value);
                  }}
                  value={wpSearchText}
                />
              </FormControl>
            </Box>
            <Box mb={{ sm: '600' }} flexGrow={{ sm: 1 }}>
              <FormControl style={{ flexGrow: 1 }} label={t`Cost Code`}>
                <SearchField
                  size={TextFieldSize.MEDIUM}
                  iconSize={IconSize.MEDIUM}
                  type="search"
                  onChange={(e) => {
                    setCostCodeSearchText(e.target.value);
                  }}
                  value={costCodeSearchText}
                />
              </FormControl>
            </Box>
            <Box mb={{ sm: '600' }} flexGrow={{ sm: 1 }}>
              <FormControl style={{ flexGrow: 1 }} label={t`Search by Commodity Tag`}>
                <SearchField
                  size={TextFieldSize.MEDIUM}
                  iconSize={IconSize.MEDIUM}
                  type="search"
                  onChange={(e) => {
                    setCommoditySearchText(e.target.value);
                  }}
                  value={commoditySearchText}
                />
              </FormControl>
            </Box>
          </Box>
        </SideSheetContentCol>
        <SideSheetContentCol style={{ flexGrow: 1, display: 'flex' }}>
          <Box flexGrow={{ sm: 1 }} flexDirection={{ sm: 'column' }}>
            {!error && costCodes.length > 0 && (
              <Box p={{ sm: '500' }} style={{ background: theme.colors.grey[200] }}>
                <Text weight="600" size="400" family="graphik">
                  <Trans>Cost Codes</Trans> ({selectedCostCodes.length} <Trans>selected</Trans>)
                </Text>
              </Box>
            )}
            {!error && costCodes.length > 0 && (
              <Box
                flexGrow={{ sm: 1 }}
                flexDirection={{ sm: 'column' }}
                style={{ overflowY: 'auto' }}
                onScroll={(e) => {
                  handleScroll(e);
                }}
              >
                {costCodes.length > 0 &&
                  map(costCodes, (costCode) => (
                    <CostCodeBoxStyled
                      isRestricted={
                        costCode.restricted ||
                        costCode.invalid ||
                        costCode.unavailable ||
                        checkSelectedCostCode(costCode)
                      }
                      key={costCode.id}
                    >
                      <LabelStyled>
                        <Checkbox
                          type="checkbox"
                          onChange={() => {
                            const selected = findIndex(selectedCostCodes, (selectedCostCode) => {
                              return selectedCostCode.id === costCode.id;
                            });
                            if (selected === -1) {
                              setSelectedCostCodes([...selectedCostCodes, costCode]);
                            } else {
                              setSelectedCostCodes(
                                filter(selectedCostCodes, (selectedCostCode) => selectedCostCode.id !== costCode.id)
                              );
                            }
                          }}
                          checked={some(selectedCostCodes, (selectedCostCode) => selectedCostCode.id === costCode.id)}
                        />
                        <Box ml={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
                          <Box>
                            <Text weight="600" size="500" family="graphik">
                              {costCode.costCode}
                            </Text>
                            &nbsp;&nbsp;
                            {costCode.restricted && (
                              <Text weight="600" size="400" color="red.800" family="graphik">
                                <Trans>(Restricted)</Trans>
                              </Text>
                            )}
                            {costCode.invalid && (
                              <Text weight="600" size="400" color="red.800" family="graphik">
                                <Trans>(Invalid)</Trans>
                              </Text>
                            )}
                            {costCode.unavailable && (
                              <Text weight="600" size="400" color="red.800" family="graphik">
                                <Trans>(Unavailable)</Trans>
                              </Text>
                            )}
                            {checkSelectedCostCode(costCode) && (
                              <Text weight="600" size="300" color="red.800" family="graphik">
                                <Trans>(Already Selected)</Trans>
                              </Text>
                            )}
                          </Box>
                          <Box mt={{ sm: '300' }}>
                            <Text weight="400" size="400" family="graphik" color="grey.600">
                              {costCode.description}
                            </Text>
                          </Box>
                        </Box>
                      </LabelStyled>
                    </CostCodeBoxStyled>
                  ))}
              </Box>
            )}

            {!loading && costCodes.length === 0 && !error && (
              <Box
                flexDirection={{ sm: 'column' }}
                justifyContent={{ sm: 'center' }}
                alignItems={{ sm: 'center' }}
                flexGrow={{ sm: 1 }}
                data-testid="cost-code-no-data"
              >
                <PackageIcon />
                <Text size={600} weight={600} family="graphik">
                  <Trans>No Cost Codes Found</Trans>
                </Text>
              </Box>
            )}

            {!loading && error && (
              <Box
                css="height: 100%;"
                justifyContent={{ sm: 'center' }}
                alignItems={{ sm: 'center' }}
                flexGrow={{ sm: 1 }}
                flexDirection={{ sm: 'column' }}
                data-testid="cost-code-error"
              >
                <Box pb={{ sm: '500' }} flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }}>
                  <Text>
                    <Trans>There was a problem loading the cost codes.</Trans>
                  </Text>
                  <Text>
                    <Trans>Please click the button below to try again.</Trans>
                  </Text>
                </Box>
                <Box>
                  <Button size={ButtonSize.LARGE} onClick={() => refetch()}>
                    <Box mr={{ sm: '400' }}>
                      <Icon icon="undo" />
                    </Box>
                    Retry
                  </Button>
                </Box>
              </Box>
            )}
            {loading && (
              <Box
                flexGrow={{ sm: 1 }}
                p={{ sm: '600' }}
                justifyContent={{ sm: 'center' }}
                data-testid="cost-code-loader"
              >
                <Spinner size={SpinnerSize.LARGE} />
              </Box>
            )}
          </Box>
        </SideSheetContentCol>
      </SideSheetBody>
      <SideSheetFooter>
        <Box justifyContent={{ sm: 'flex-end' }}>
          <Box mr={{ sm: '400' }}>
            <DrawerFooterButtonStyled onClick={closeSidesheet} appearance={ButtonAppearance.SECONDARY}>
              <Trans>Cancel</Trans>
            </DrawerFooterButtonStyled>
          </Box>
          <Box>
            <DrawerFooterButtonStyled
              disabled={selectedCostCodes.length === 0}
              appearance={ButtonAppearance.SECONDARY}
              onClick={addSelectedCostCode}
            >
              <Trans>Add Cost Code(s)</Trans>
            </DrawerFooterButtonStyled>
          </Box>
        </Box>
      </SideSheetFooter>
    </SideSheet>
  );
};
